import { unref, watch, type WatchStopHandle } from 'vue';

import { type EvaluateRouteAccess } from '@bv/plugins/router';
import { useUserStore } from '@bv/stores';
import { restoreFromMaintenance } from '@bv/utils/maintenance/block';

import type { Router } from 'vue-router';

/**
 * Watches permission changes and route meta.canAccess changes, re-evaluating
 * the current route's accessibility using the shared route access evaluator.
 *
 * This watcher triggers when:
 * - Permissions change (e.g., role updates, workspace switches)
 * - The current route's meta.canAccess changes dynamically
 *
 * Route navigation is already handled by the `beforeEach` guard, so we only
 * watch the specific properties we need (not the entire route object) to avoid
 * expensive deep watching and redundant evaluations.
 *
 * @param router - The Vue Router instance to monitor
 * @param evaluateRouteAccess - Shared access evaluation function
 * @returns An object with a `stop` method to stop watching permissions
 *
 * @example
 * ```ts
 * import { useWatchRouterPermissions } from '@bv/composables';
 *
 * const router = createRouter({ ... }) as RouterWithAccessEvaluator;
 * const { stop } = useWatchRouterPermissions(router, router.evaluateRouteAccess);
 *
 * // Later, to stop watching:
 * stop();
 * ```
 */
export const useWatchRouterPermissions = (
  router: Router,
  evaluateRouteAccess: EvaluateRouteAccess,
): { stop: WatchStopHandle } => {
  const userStore = useUserStore();

  // Watch permissions and route meta.canAccess - lightweight watching of only
  // the specific properties we need, avoiding expensive deep watching
  // Properly unwraps refs/computed values and evaluates functions to watch
  // the actual reactive value, not just the reference
  const stopWatcher = watch(
    () => {
      const route = router.currentRoute.value;
      const canAccessValue = route.meta.canAccess;

      // If canAccess is a ref/computed, unwrap it; if it's a function, call it and unwrap
      // This ensures we're watching the actual reactive value, not just the reference
      let canAccess: boolean | undefined;
      if (canAccessValue !== undefined && canAccessValue !== null) {
        if (typeof canAccessValue === 'function') {
          const result = canAccessValue();
          const unwrapped = unref(result);
          canAccess = typeof unwrapped === 'boolean' ? unwrapped : undefined;
        } else {
          const unwrapped = unref(canAccessValue);
          canAccess = typeof unwrapped === 'boolean' ? unwrapped : undefined;
        }
      }

      return {
        permissions: userStore.permissions,
        canAccess,
        routePath: route.fullPath, // Track route path to avoid re-evaluation on navigation
      };
    },
    async (newValue, oldValue) => {
      // Skip initial trigger where there is no previous value
      if (!oldValue) {
        return;
      }

      // Only re-evaluate if permissions or canAccess changed, not on route navigation
      // (route navigation is already handled by beforeEach)
      if (
        newValue.permissions === oldValue.permissions &&
        newValue.canAccess === oldValue.canAccess &&
        newValue.routePath === oldValue.routePath
      ) {
        return;
      }

      try {
        const currentRoute = router.currentRoute.value;
        const decision = await evaluateRouteAccess(currentRoute);

        if (decision.type === 'allow') {
          return;
        }

        if (decision.type === 'reload') {
          restoreFromMaintenance();
          return;
        }

        // Avoid simple infinite loops by not re-pushing the same location
        if (currentRoute.fullPath === router.resolve(decision.to).fullPath) {
          return;
        }

        await router.push(decision.to);
      } catch (error) {
        // In case of unexpected failures, log and fallback to a safe redirect
        // instead of leaving the user on a potentially unauthorized route.
        throw new Error(`[useWatchRouterPermissions] Failed to re-evaluate route access: ${error}`);
      }
    },
  );

  return {
    stop: stopWatcher,
  };
};
